import Events from "./Events.js";

class Loader {
  constructor(assets) {
    // console.log("Loader created with assets:", assets, assets.length);
    this.assets = assets; // Array of asset objects with type and URL
    this.totalAssets = assets.length;
    this.loadedAssets = 0;
    this.loadingScreen = null;
    this.startTime = null;
  }

  preload() {
    if (this.totalAssets === 0) {
      this.allAssetsLoaded();
      return;
    }

    this.showLoadingScreen();
    this.startTime = performance.now(); // Capture the start time

    this.assets.forEach((asset) => {
      // console.log("Preloading asset:", asset);
      if (asset.type === "image") {
        this.preloadImage(asset.url);
      } else if (asset.type === "video") {
        this.preloadVideo(asset.url);
      } else {
        console.error(`Unsupported asset type: ${asset.type}`);
      }
    });
  }

  preloadImage(url) {
    const img = new Image();
    img.src = url;
    img.onload = () => this.assetLoaded();
    img.onerror = () => {
      const error = `Failed to load image: ${url}`;
      console.error(error);
      this.showErrorMessage(error);
    };
  }

  preloadVideo(url) {
    // Check if video is already preloaded
    if (this.getPreloadedVideo(url)) {
      return;
    }

    // Fetch the video via AJAX
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch video: ${url}`);
        }
        return response.blob(); // Convert the response to a Blob
      })
      .then((blob) => {
        const videoBlobUrl = URL.createObjectURL(blob); // Create a Blob URL

        // Create a video element
        const video = document.createElement("video");
        video.src = videoBlobUrl;
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.currentTime = 4.5;

        // Store the video blob and URL in memory
        this.storePreloadedVideo(url, {
          video,
          blob,
          videoBlobUrl,
        });

        video.oncanplaythrough = () => {
          video.preloaded = true;
          this.assetLoaded();
        };

        // Handle video load errors
        video.onerror = () => {
          console.error(`Failed to load video from Blob URL: ${url}`);
          this.showErrorMessage(`Failed to load video: ${url}`);
        };
      })
      .catch((error) => {
        console.error(error.message);
      });
  }

  storePreloadedVideo(url, videoData) {
    if (!this.preloadedVideos) {
      this.preloadedVideos = {};
    }

    // Store the video data (which includes video element, Blob, and Blob URL)
    this.preloadedVideos[url] = videoData;
  }

  getPreloadedVideo(url) {
    // Return the stored video data (video element, Blob, and Blob URL)
    return this.preloadedVideos ? this.preloadedVideos[url] : null;
  }

  assetLoaded() {
    this.loadedAssets += 1;
    this.updateLoadingScreen();

    if (this.loadedAssets === this.totalAssets) {
      this.allAssetsLoaded();
    }
  }

  allAssetsLoaded() {
    // Check if any video failed to load and retry or handle gracefully
    const missingVideos = this.assets.filter(
      (asset) => asset.type === "video" && !this.getPreloadedVideo(asset.url)
    );

    if (missingVideos.length > 0) {
      console.log("Some videos are missing, waiting for them to preload...");
      console.log("Missing videos:", missingVideos);
      // Optionally, handle missing videos (e.g., retry or notify user)
      return;
    }

    this.hideLoadingScreen();
    const endTime = performance.now(); // Capture the end time
    const loadTime = ((endTime - this.startTime) / 1000).toFixed(2); // Calculate load time in seconds
    console.log(`All assets loaded in ${loadTime} seconds.`);
    Events.emit("assets.loaded"); // Emit the event when all assets are loaded
  }

  showLoadingScreen(error) {
    // TODO - refactor this to use the persona svg
    this.loadingScreen = document.createElement("div");
    this.loadingScreen.id = "loading-screen";
    this.loadingScreen.style.position = "fixed";
    this.loadingScreen.style.top = 0;
    this.loadingScreen.style.left = 0;
    this.loadingScreen.style.width = "100%";
    this.loadingScreen.style.height = "100%";
    this.loadingScreen.style.color = "#fff";
    this.loadingScreen.style.fontFamily = "CommonGround_Regular", "sans-serif";
    this.loadingScreen.style.display = "flex";
    this.loadingScreen.style.justifyContent = "center";
    this.loadingScreen.style.alignItems = "center";
    this.loadingScreen.style.fontSize = "16px";
    this.loadingScreen.style.zIndex = 9999;
    this.loadingScreen.innerHTML = `Loading... 0%`;

    document.body.appendChild(this.loadingScreen);
  }

  updateLoadingScreen() {
    const percent = Math.floor((this.loadedAssets / this.totalAssets) * 100);
    this.loadingScreen.innerHTML = `Loading... ${percent}%`;
  }

  hideLoadingScreen() {
    if (this.loadingScreen) {
      this.loadingScreen.style.transition = "opacity 0.5s";
      this.loadingScreen.style.opacity = "0";
      setTimeout(() => {
        document.body.removeChild(this.loadingScreen);
      }, 500);
    }
  }

  showErrorMessage(error) {
    // if there is an error, show it on the loading screen
    this.loadingError = document.createElement("p");
    this.loadingError.id = "loading-error";
    this.loadingError.style.display = "block";
    this.loadingError.style.color = "red";
    this.loadingError.style.fontSize = "1rem";
    this.loadingError.style.position = "absolute";
    this.loadingError.style.bottom = "10%";
    this.loadingError.style.left = "50%";
    this.loadingError.style.transform = "translate(-50%, -50%)";
    this.loadingError.style.zIndex = 9999;
    this.loadingError.innerHTML = error;
    document.body.appendChild(this.loadingError);
  }

  hideErrorMessage() {
    if (this.loadingError) {
      document.body.removeChild(this.loadingError);
    }
  }
}

export default Loader;
